/**
 * MÓDULO 11: Validación Estricta CUIT/CUIL
 * Lanza Boundary Error si el dato no supera validación
 * Integrado con OpenCode para debugging
 */

// NO importamos de validaciones.js para evitar errores de ruta en Windows
// Las funciones se definen aquí directamente

/**
 * Normaliza CUIT/CUIL - Limpieza agresiva de caracteres no numéricos
 * Si el usuario ingresa solo DNI (8 dígitos), autocompleta prefijo y dígito verificador
 * @param {string} cuit - CUIT/CUIL o DNI de 8 dígitos
 * @param {string} genero - 'M' para hombres, 'F' para mujeres (necesario para autocompletar DNI)
 * @returns {string} CUIT normalizado (11 dígitos) o cadena vacía
 */
function normalizeCUIT(cuit, genero = null) {
  if (!cuit) return ""
  // Limpieza agresiva de caracteres no numéricos
  const clean = cuit.toString().replace(/[^\d]/g, "")
  
  // ✅ TOLERANCIA: Si ingresa solo DNI (8 dígitos), autocompletar
  if (clean.length === 8 && genero) {
    // Determinar prefijo: 20 para hombres, 27 para mujeres (según especificación usuario)
    const prefix = genero === 'M' ? '20' : '27'
    const base = prefix + clean  // 10 dígitos base
    
    // Calcular dígito verificador usando Módulo 11
    const factores = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]
    let suma = 0
    for (let i = 0; i < 10; i++) {
      suma += parseInt(base[i]) * factores[i]
    }
    const resto = suma % 11
    const digitoVerificador = resto === 0 ? 0 : resto === 1 ? 9 : 11 - resto
    
    const cuitCompleto = base + digitoVerificador
    
    // Validar contra Módulo 11 antes de retornar
    const validacion = { valido: false }
    if (cuitCompleto.length === 11) {
      const factoresCheck = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]
      let sumaCheck = 0
      for (let i = 0; i < 10; i++) {
        sumaCheck += parseInt(cuitCompleto[i]) * factoresCheck[i]
      }
      const restoCheck = sumaCheck % 11
      const dvEsperado = restoCheck === 0 ? 0 : restoCheck === 1 ? 9 : 11 - restoCheck
      const dvRecibido = parseInt(cuitCompleto[10])
      validacion.valido = dvEsperado === dvRecibido
    }
    
    if (validacion.valido) {
      return cuitCompleto
    }
    return ""  // No pasó validación Módulo 11
  }
  
  if (clean.length !== 11) return ""
  return clean
}

/**
 * Validación estricta de CUIT/CUIL para reporte judicial
 * Si no pasa validación → Boundary Error
 * @param {string} cuit - CUIT/CUIL a validar
 * @param {Object} contexto - { origen: 'reporteJudicial' | 'formulario', datosCompletos: boolean }
 * @returns {Object} { valido, cuitNormalizado, error }
 */
export function validarCUITModulo11(cuit, contexto = {}) {
  const { origen = 'desconocido', datosCompletos = false } = contexto
  
  // Validación básica usando la función local
  const clean = normalizeCUIT(cuit)
  const resultado = { valido: false, error: null }
  
  if (!clean || clean.length !== 11) {
    resultado.valido = false
    resultado.error = 'CUIT debe tener exactamente 11 dígitos'
  } else {
    // Validar dígito verificador Módulo 11
    const factores = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]
    let suma = 0
    for (let i = 0; i < 10; i++) {
      const digito = parseInt(clean[i])
      suma += digito * factores[i]
    }
    
    const resto = suma % 11
    const digitoVerificador = resto === 0 ? 0 : resto === 1 ? 9 : 11 - resto
    const dvCalculado = parseInt(clean[10])
    
    if (dvCalculado === digitoVerificador) {
      resultado.valido = true
      resultado.cuitNormalizado = `${clean.substring(0, 2)}-${clean.substring(2, 10)}-${clean[10]}`
    } else {
      resultado.valido = false
      resultado.error = `Dígito verificador inválido. Esperado: ${digitoVerificador}, Recibido: ${dvCalculado}`
    }
  }
  
  // Si no pasa validación → Boundary Error
  if (!resultado.valido) {
    const boundaryError = {
      tipo: 'BOUNDARY_ERROR',
      modulo: 'Módulo 11 - Validación CUIT/CUIL',
      mensaje: `CUIT/CUIL no supera validación: ${resultado.error}`,
      datoRecibido: cuit,
      origen,
      datosCompletos,
      timestamp: new Date().toISOString(),
      solucion: 'Verificar formato XX-XXXXXXXX-X o XXXXXXXXXX. Ejecutar normalizeCUIT() antes de enviar.'
    }
    
    // Log estructurado para OpenCode
    console.error('🚨 BOUNDARY ERROR [Módulo 11]:', JSON.stringify(boundaryError, null, 2))
    
    // En entorno Node.js, escribir a stderr para OpenCode
    if (typeof process !== 'undefined' && process.stderr) {
      process.stderr.write(`[BOUNDARY_ERROR] Módulo 11: ${JSON.stringify(boundaryError)}\n`)
    }
    
    return {
      valido: false,
      cuitNormalizado: null,
      error: boundaryError,
      debeBloquearPDF: true
    }
  }
  
  // Normalización: asegurar formato XX-XXXXXXXX-X
  let cuitNormalizado = normalizeCUIT(cuit)
  if (cuitNormalizado && !cuitNormalizado.includes('-')) {
    cuitNormalizado = `${cuitNormalizado.substring(0, 2)}-${cuitNormalizado.substring(2, 10)}-${cuitNormalizado[10]}`
  }
  
  return {
    valido: true,
    cuitNormalizado: resultado.cuitNormalizado || cuitNormalizado,
    error: null,
    debeBloquearPDF: false
  }
}

/**
 * Normaliza CUIT/CUIL antes de procesamiento (versión pública)
 * Si el usuario ingresa solo DNI (8 dígitos), autocompleta prefijo y dígito verificador
 * @param {string} cuit - CUIT/CUIL o DNI de 8 dígitos
 * @param {string} genero - 'M' para hombres, 'F' para mujeres (necesario para autocompletar DNI)
 * @returns {string} CUIT normalizado XX-XXXXXXXX-X
 */
export function normalizeCUITPublic(cuit, genero = null) {
  const normalizado = normalizeCUIT(cuit, genero)
  if (!normalizado || normalizado.length !== 11) return ''
  
  return `${normalizado.substring(0, 2)}-${normalizado.substring(2, 10)}-${normalizado[10]}`
}

export { normalizeCUIT }
